import produce from 'immer';
import isNil from 'lodash/isNil';
import differenceWith from 'lodash/differenceWith';

import {
  ChartType,
  DATASET_MIXPANEL,
  MATH_TYPE,
  PRIMITIVE_MATH_TYPES,
  ValueRepresentationType,
  XAU_MATH_TYPES,
  XAU_ONLY_MATH_TYPES,
} from 'common/report/constants';
import {
  AnalysisType,
  ChartPlotStyle,
  DisplayOptions,
  InsightsBookmarkParams,
  InsightsChartType,
  InsightsResourceType,
} from 'common/report/insights/util/types';
import {TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {insightsFilterAttrsFromPropertyFilter} from 'common/widgets/property-filter-menu/models/insights-filter';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {isLiftEnabled} from 'common/report/util/lift';
import {LiftType} from 'common/report/util/lift/types';
import {Section, TimeSection} from 'common/report/insights/models/section';
import {CommonDataTableFields} from 'common/components/data-table/types';
import {useInsightsNewCountMenu} from 'common/report/util/feature-sampling';

import {getPropertyFiltersFromBookmark} from './filters';
import {getBookmarkTimeClauseForDateRange, getDateRange} from './time';
import {fillMetricColumnSort} from './';

function getDisplayOptionOverridesForChartType(chartType: ChartType): Pick<DisplayOptions, 'chartType' | 'plotStyle'> {
  switch (chartType) {
    case ChartType.Bar:
      return {
        chartType: ChartType.Bar,
        plotStyle: ChartPlotStyle.Standard,
      };
    case ChartType.StackedBar:
      return {
        chartType: ChartType.Bar,
        plotStyle: ChartPlotStyle.Stacked,
      };
    case ChartType.Line:
      return {
        chartType: ChartType.Line,
        plotStyle: ChartPlotStyle.Standard,
      };
    case ChartType.StackedLine:
      return {
        chartType: ChartType.Line,
        plotStyle: ChartPlotStyle.Stacked,
      };
    case ChartType.Pie:
      return {
        chartType: ChartType.Pie,
        plotStyle: ChartPlotStyle.Standard,
      };
    case ChartType.Table:
      return {
        chartType: ChartType.Table,
        plotStyle: ChartPlotStyle.Standard,
      };
    case ChartType.Metric:
      return {
        chartType: ChartType.Metric,
        plotStyle: ChartPlotStyle.Standard,
      };
    default: {
      throw new Error(`unknown Insights chart type "${chartType}"`);
    }
  }
}

// It's important that these override methods return a whole bookmark params
// so that it does not require additional knowledge of the bookmark format to use effectively
export function getBookmarkParamsWithChartType(
  bookmarkParams: InsightsBookmarkParams,
  chartTypeOverride: ChartType,
): InsightsBookmarkParams {
  return {
    ...bookmarkParams,
    displayOptions: {
      ...bookmarkParams.displayOptions,
      ...getDisplayOptionOverridesForChartType(chartTypeOverride),
    },
  };
}

// It's important that these override methods return a whole bookmark params
// so that it does not require additional knowledge of the bookmark format to use effectively
export function getBookmarkParamsWithDateRange(
  bookmarkParams: InsightsBookmarkParams,
  dateRangeOverride: DateRange,
  timeUnitOverride?: TimeUnit,
): InsightsBookmarkParams {
  const currentTimeSection = Section.deserialize(TimeSection.TYPE, bookmarkParams.sections.time);
  const currentDateRange = getDateRange(currentTimeSection.clauses);

  if (currentDateRange.equals(dateRangeOverride)) {
    return bookmarkParams;
  }

  const newTimeClause = getBookmarkTimeClauseForDateRange(dateRangeOverride);
  if (timeUnitOverride) {
    newTimeClause.unit = timeUnitOverride;
  }
  const showClauses = bookmarkParams.sections.show;
  // As of writing, arb doesn't support Cohorts over Time queries with anything finer than Day for perf reasons
  const hasCohortsOverTime = showClauses.some((showClause) => showClause.resourceType === InsightsResourceType.Cohorts);
  const invalidTimeUnits: TimeUnit[] = [TimeUnit.Hour, TimeUnit.Minute];
  if (hasCohortsOverTime && invalidTimeUnits.includes(newTimeClause.unit)) {
    newTimeClause.unit = TimeUnit.Day;
  }
  const hasXauClause = showClauses.some((clause) => (XAU_ONLY_MATH_TYPES as MATH_TYPE[]).includes(clause.math));
  if (hasXauClause && invalidTimeUnits.includes(newTimeClause.unit)) {
    newTimeClause.unit = TimeUnit.Day;
  }
  return {
    ...bookmarkParams,
    sections: {
      ...bookmarkParams.sections,
      time: [newTimeClause],
    },
  };
}

// It's important that these override methods return a whole bookmark params
// so that it does not require additional knowledge of the bookmark format to use effectively
export function getBookmarkParamsWithAdditionalFilters(
  bookmarkParams: InsightsBookmarkParams,
  filters: PropertyFilter[],
): InsightsBookmarkParams {
  const currentFilters = getPropertyFiltersFromBookmark(bookmarkParams.sections.filter);

  // Don't add duplicate filters
  const filtersToAppend = differenceWith(filters, currentFilters, (f1, f2) => f1.equals(f2));
  const insightsFilters: TODO[] = filtersToAppend.map((propertyFilter) => {
    return insightsFilterAttrsFromPropertyFilter({dataset: DATASET_MIXPANEL, ...propertyFilter});
  });

  return {
    ...bookmarkParams,
    sections: {
      ...bookmarkParams.sections,
      filter: [...bookmarkParams.sections.filter, ...insightsFilters],
    },
  };
}

/**
 * Converts relative value display to the appropriate LiftComparison
 * and sets value display to absolute, ONLY if the Lift feature flag is enabled for the project.
 *
 * @param {InsightsBookmarkParams} bookmarkParams: The parameters needing a check/update.
 * @returns {InsightsBookmarkParams} a copy of the passed-in params, with the update made.
 */
export function getBookmarkParamsWithRelativeValueUpdated(
  bookmarkParams: InsightsBookmarkParams,
): InsightsBookmarkParams {
  // It's important that these override methods return a whole bookmark params
  // so that it does not require additional knowledge of the bookmark format to use effectively

  // value representation check repeated here, to avoid cloning params we won't change.
  if (isLiftEnabled() && bookmarkParams.displayOptions?.value === ValueRepresentationType.Relative) {
    const newParams = {
      ...bookmarkParams,
    };

    updateRelativeDisplayToLiftComparison(newParams);
    return newParams;
  } else {
    return bookmarkParams;
  }
}

/**
 * Updates a partial InsightsBookmarkParams to have an "Overall" LiftComparison, if the `displayOptions.value` is Relative.
 *
 * WARNING: Modifies the params in-place. Only pass in cloned params.
 * Only call this if Lift is enabled for a project.
 *
 * Use this to convert params on-the-fly, until we do a bookmark migration for the Lift feature.
 *
 * @param {Partial<InsightsBookmarkParams>} bookmarkParams Must have `displayOptions` for this method to do anything.
 */
export function updateRelativeDisplayToLiftComparison(bookmarkParams: Partial<InsightsBookmarkParams>) {
  if (bookmarkParams.displayOptions?.value === ValueRepresentationType.Relative) {
    // presume that if they already have a liftComparison, it's what the user wants
    if (!bookmarkParams.liftComparison) {
      bookmarkParams.liftComparison = {
        type: LiftType.Overall,
      };
    }
    bookmarkParams.displayOptions.value = ValueRepresentationType.Absolute;
  }
}

/**
 * This transformation of sorting params is needed to populate missing sort fields for the "sort by column" mode
 * historically certain chart types have omitted rendering the metric column for single segmented metric reports
 * whenever a report is loaded with that configuration, we want to maintain any previous sorting state after
 * injecting the metric column into the results
 */
export function updateSortConfig(
  params: InsightsBookmarkParams,
  {suppressMigrationAlert = true}: {suppressMigrationAlert?: boolean} = {},
) {
  if (isNil(params.sorting)) {
    return params;
  }

  const groupByCount = (params.sections.cohorts?.length ? 1 : 0) + params.sections.group?.length ?? 0;
  const hasHistogram = params.sections.show?.some((entry) => entry.math === PRIMITIVE_MATH_TYPES.HISTOGRAM);
  const headerCount = 1 + groupByCount + (hasHistogram ? 1 : 0);

  const defaultSortValue = {
    [ChartType.Line]: CommonDataTableFields.Average,
    [ChartType.Pie]: CommonDataTableFields.Total,
    [ChartType.Metric]: CommonDataTableFields.Total,
  };

  return produce(params, (draft) => {
    for (const type of [ChartType.Line, ChartType.Pie, ChartType.Metric]) {
      if (draft.sorting[type]) {
        draft.sorting[type] = fillMetricColumnSort(
          type,
          headerCount,
          draft.sorting[type],
          defaultSortValue[type],
          suppressMigrationAlert,
        );
      }
    }
  });
}

/**
 * Moving rolling and cumulative from report level to metric level. Each show clause will
 * hold an information of rolling and cumulative so that it is possible to apply such count option
 * to each metric.
 */
export function pushRollingCumulativeToShow(bookmarkParams: InsightsBookmarkParams) {
  if (!useInsightsNewCountMenu()) {
    return bookmarkParams;
  }

  const analysis = bookmarkParams.displayOptions?.analysis;
  const showSection = bookmarkParams.sections.show.map((clause) => {
    const hasXauClause = XAU_ONLY_MATH_TYPES.includes(clause.math as XAU_MATH_TYPES);
    const isUnique = clause.math === PRIMITIVE_MATH_TYPES.UNIQUE;
    let rolling = clause.rolling || null;
    let cumulative = Boolean(clause.cumulative);
    let math = clause.math;
    if (analysis === AnalysisType.Rolling) {
      // If the clause has XAU or unique math, set rolling to null since XAU itself already implies rolling and we don't support rolling + unique anymore
      // Else set the rolling window size from displayOptions
      rolling =
        !isUnique && !hasXauClause
          ? {
              rollingWindowSize: bookmarkParams.displayOptions?.rollingWindowSize,
            }
          : null;
    }
    if (analysis === AnalysisType.Cumulative) {
      cumulative = true;
      // if the clause has XAU math and is cumulative, convert math type to Unique (existing XAU + Cumulative converted to Unique + Cumulative)
      if (hasXauClause) math = PRIMITIVE_MATH_TYPES.UNIQUE;
    }
    return {
      ...clause,
      math,
      rolling,
      cumulative,
    };
  });
  return {
    ...bookmarkParams,
    displayOptions: {
      ...bookmarkParams.displayOptions,
      ...([AnalysisType.Rolling, AnalysisType.Cumulative].includes(analysis) && {analysis: AnalysisType.Linear}),
      ...([AnalysisType.Rolling, AnalysisType.Cumulative].includes(analysis) && {
        chartType: ChartType.Line as InsightsChartType,
      }),
    },
    sections: {
      ...bookmarkParams.sections,
      show: showSection,
    },
  };
}
